import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { FetchAccountTheme } from "../services/account";
import { getUserType } from "../utils/auth-helpers";
import { isActorInvitePath } from "../utils/account-context";
import {
  DEFAULT_ACCOUNT_THEME,
  getAccountTheme,
  normalizeAccountTheme,
  saveAccountTheme,
} from "../utils/account-branding-theme";

export const useAccountBranding = (accountId) => {
  const location = useLocation();
  const isActorInviteAuth = isActorInvitePath(location.pathname);
  const activeAccountIdRef = useRef(accountId);
  activeAccountIdRef.current = accountId;

  const [branding, setBranding] = useState(() => getAccountTheme(accountId));
  const [isLoading, setIsLoading] = useState(Boolean(accountId));
  const [error, setError] = useState(null);

  const refreshBranding = useCallback(async () => {
    if (isActorInviteAuth) {
      const defaultBranding = normalizeAccountTheme(DEFAULT_ACCOUNT_THEME);
      setBranding(defaultBranding);
      setError(null);
      setIsLoading(false);
      return defaultBranding;
    }

    const requestAccountId = accountId;
    const fallback = getAccountTheme(requestAccountId);
    setBranding(fallback);
    setError(null);

    if (!requestAccountId) {
      setIsLoading(false);
      return fallback;
    }

    if (getUserType() === "actor") {
      const local = getAccountTheme(requestAccountId);
      setBranding(local);
      setIsLoading(false);
      return local;
    }

    setIsLoading(true);
    const response = await FetchAccountTheme(requestAccountId);
    if (activeAccountIdRef.current !== requestAccountId) {
      return fallback;
    }

    if (response?.error) {
      setError(response.error);
      setIsLoading(false);
      return fallback;
    }

    const nextTheme = saveAccountTheme(requestAccountId, response?.branding_theme || fallback);
    setBranding(nextTheme);
    setIsLoading(false);
    return nextTheme;
  }, [accountId, isActorInviteAuth]);

  const patchBranding = useCallback(
    (patch) => {
      setBranding((prev) => normalizeAccountTheme({ ...prev, ...patch }));
    },
    [setBranding]
  );

  useEffect(() => {
    refreshBranding();
  }, [refreshBranding]);

  return {
    branding,
    setBranding,
    patchBranding,
    refreshBranding,
    isLoading,
    error,
  };
};

export default useAccountBranding;
